// resources/js/Pages/Formula/questions/QuestionManager.js

import React, { useState, useRef } from 'react';
import { Card, Button } from 'react-bootstrap';
import QuestionModal from './QuestionModal';
import QuestionList from './QuestionList';
import { useQuestions } from './questionHooks';

export default function QuestionManager({ 
  questions = [], 
  onQuestionsChange, 
  promptText = '',
  onPromptUpdate 
}) {
  const {
    questions: localQuestions,
    modalOpen,
    editingQuestion,
    editingIndex,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    reorderQuestions,
    openAddModal,
    openEditModal,
    closeModal,
    validateQuestion
  } = useQuestions(questions, onPromptUpdate);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const lastSyncedRef = useRef('');

  // ✅ FIXED: Only sync when questions actually change
  React.useEffect(() => {
    const currentQuestionsString = JSON.stringify(localQuestions);
    if (currentQuestionsString !== lastSyncedRef.current) {
      console.log('🔄 Questions changed, syncing to parent:', localQuestions.length);
      onQuestionsChange?.(localQuestions);
      lastSyncedRef.current = currentQuestionsString;
    }
  }, [localQuestions, onQuestionsChange]);

  // Handle question save (add or update)
  const handleSave = (questionData) => {
    if (editingIndex !== null) {
      updateQuestion(editingIndex, questionData, promptText);
    } else {
      addQuestion(questionData);
    }
  };

  // Handle delete with confirmation
  const handleDeleteClick = (index) => {
    setShowDeleteConfirm(index);
  };

  const confirmDelete = () => {
    if (showDeleteConfirm !== null) {
      deleteQuestion(showDeleteConfirm);
      setShowDeleteConfirm(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  return (
    <>
      <Card className="mt-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-0">Questions ({localQuestions.length})</h6>
            {localQuestions.length > 1 && (
              <small className="text-muted">
                💡 Drag questions to reorder them
              </small>
            )}
          </div>
          <Button size="sm" variant="outline-primary" onClick={openAddModal}>
            + Add Question
          </Button>
        </Card.Header>
        
        <Card.Body className="p-0">
          <QuestionList
            questions={localQuestions}
            onEdit={openEditModal}
            onDelete={handleDeleteClick}
            onReorder={reorderQuestions}
            showDeleteConfirm={showDeleteConfirm}
            onConfirmDelete={confirmDelete}
            onCancelDelete={cancelDelete}
          />
        </Card.Body>
      </Card>

      <QuestionModal
        show={modalOpen}
        onHide={closeModal}
        onSave={handleSave}
        editingQuestion={editingQuestion}
        validateQuestion={(data) => validateQuestion(data, editingIndex)}
      />
    </>
  );
}